import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import FlightDealDuration from 'components/Flights/FlightMerchandising/FlightDealDuration/FlightDealDuration'
import FlightDealAgentHubDisclaimer from 'components/Flights/FlightMerchandising/FlightDealAgentHubDisclaimer/FlightDealAgentHubDisclaimer'
import { isAgentHubEnabled } from 'agentHub/selectors/agentHubSelectors'
import DropdownSheet from 'LuxKit/Dropdown/Sheet/DropdownSheet'
import BodyText from 'LuxKit/Typography/BodyText'
import Group from 'components/utils/Group'
import { FLIGHTS_MAX_LEAD_TIME_DAYS, FLIGHTS_MIN_LEAD_TIME_DAYS, FlightsFareTypes } from 'constants/flight'
import FlightSearchWidgetDispatchContext from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetDispatchContext'
import FlightSearchWidgetStateContext from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetStateContext'
import { FlightSearchWidgetActions } from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetStateReducer'
import useFeaturedFlightDeals from 'hooks/Flights/useFeaturedFlightDeals'
import { addDays } from 'lib/datetime/dateUtils'
import type { ComponentProps } from 'react'
import React, {
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react'
import { useIntl } from 'react-intl'
import FormattedFlightSearchDate from './FlightSearchMenu/FormattedFlightSearchDate'
import FlightsDatePicker from './FlightsDatePicker/FlightsDatePicker'

interface Props
  extends Pick<ComponentProps<typeof DropdownSheet>, 'anchorRef'> {
  id: string
  flightDeals?: Array<App.FlightDeal>
  onDepartingClick?: () => void
  onArrivalClick?: () => void
  onClose?: () => void
  isOpen: boolean
  onDateChange?: (date: Date) => void
}

const earliestBookingDate = addDays(new Date(), FLIGHTS_MIN_LEAD_TIME_DAYS)
const finalBookingDate = addDays(new Date(), FLIGHTS_MAX_LEAD_TIME_DAYS)

function FlightSearchDateSelectSheet(props: Props) {
  const { anchorRef, id, onClose, isOpen, onDateChange, flightDeals } = props
  const state = useContext(FlightSearchWidgetStateContext)
  const dispatch = useContext(FlightSearchWidgetDispatchContext)
  const intl = useIntl()

  const currentFlight = useMemo(() => {
    return state.flights.find((flight) => flight.id === id)
  }, [state.flights, id])

  const { checkinDate, checkoutDate, departureAirport, arrivalAirport } = currentFlight!
  const { fareType, showSegmentActiveDeals, flightDealId } = state

  useEffect(() => {
    if (fareType.value === FlightsFareTypes.ONE_WAY) {
      dispatch({ type: FlightSearchWidgetActions.SET_CHECKOUT_DATE, date: null })
    }
  }, [dispatch, fareType.value])

  const { featuredRanges, displayDeal } = useFeaturedFlightDeals({
    flightDeals,
    departureAirport,
    arrivalAirport,
    fareType: fareType.value,
    showSegmentActiveDeals,
    flightDealId,
  })

  const showDate = useMemo(() => {
    if (fareType.value === FlightsFareTypes.ONE_WAY) {
      return checkinDate?.isValid()
    }

    return fareType.value === FlightsFareTypes.RETURN
  }, [checkinDate, fareType.value])

  const departingFormField = useRef<HTMLButtonElement | null>(null)

  const applyText = useMemo(
    () =>
      intl.formatMessage({
        id: 'qjqe3TY2rA',
        defaultMessage: 'Apply',
        description: 'Button text to apply flight date selection changes',
      }),
    [intl],
  )

  const cancelText = useMemo(
    () =>
      intl.formatMessage({
        id: 'TaaC62B2s1',
        defaultMessage: 'Cancel',
        description: 'Button text to cancel flight date selection',
      }),
    [intl],
  )

  const priceInfoText = useMemo(
    () =>
      intl.formatMessage({
        id: 'MeAUltVLii',
        defaultMessage: 'Based on recent return prices (per adult) found',
        description: 'Information text about pricing basis for flight date selection',
      }),
    [intl],
  )

  return (
    <DropdownSheet
      size="fill-anchor"
      open={isOpen}
      anchorRef={anchorRef}
      triggerRef={departingFormField}
      onClose={onClose}
      primaryActionProps={{
        'data-testid': 'apply-flight-search-changes',
        type: 'submit',
        children: applyText,
        onClick: onClose,
      }}
      secondaryActionProps={{
        kind: 'tertiary',
        children: cancelText,
        onClick: onClose,
      }}
      footerStart={
        <Group
          direction="horizontal"
          horizontalAlign="space-between"
          verticalAlign="center"
        >
          <BodyText variant="small" colour="neutral-two">
            {priceInfoText}
          </BodyText>

          {showDate && (
            <FormattedFlightSearchDate
              checkInDate={checkinDate}
              checkOutDate={checkoutDate}
              fareType={fareType.value}
            />
          )}
        </Group>
      }
    >
      <VerticalSpacer gap={24} key={id}>
        {displayDeal && !isAgentHubEnabled && <FlightDealDuration flightDeal={displayDeal} />}
        {isAgentHubEnabled && <FlightDealAgentHubDisclaimer />}

        <FlightsDatePicker
          onDateChange={onDateChange}
          minDate={earliestBookingDate}
          maxDate={finalBookingDate}
          type="dual"
          id={id}
          featuredRanges={featuredRanges}
          flightDeals={flightDeals}
        />
      </VerticalSpacer>
    </DropdownSheet>
  )
}

export default FlightSearchDateSelectSheet
